import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { showToast } from '../../components/Toast';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const sessionId = searchParams.get('session_id');
        
        if (!sessionId) {
          setStatus('error');
          setMessage('No session ID found. Please contact support.');
          return;
        }

        console.log('🔍 Verifying payment for session:', sessionId);

        // Call the verify-payment endpoint
        const response = await apiClient.post('/billing/verify-payment', {
          sessionId,
        });

        console.log('✅ Payment verification response:', response.data);

        if (response.data?.success || response.data?.data?.success) {
          setStatus('success');
          setMessage('Payment successful! Your subscription is now active.');
          
          // Redirect to members page after 2 seconds
          setTimeout(() => {
            navigate('/members');
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Payment verification failed. Please contact support.');
          showToast('Payment verification failed', 'error', 'Please contact support');
        }
      } catch (error: any) {
        console.error('❌ Payment verification error:', error);
        setStatus('error');
        
        // Map backend errors to user-friendly messages
        const errorMessage = error.response?.data?.message || error.message || 'Payment verification failed';
        setMessage(errorMessage);
        showToast('Payment verification failed', 'error', errorMessage);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#09140F] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-[#16251D] border border-gray-800 rounded-lg p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="flex justify-center mb-6">
                <Loader size={48} className="text-[#23F47C] animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
              <p className="text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-6">
                <CheckCircle size={48} className="text-[#23F47C]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              <p className="text-gray-500 text-sm">Redirecting to members page...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-6">
                <AlertCircle size={48} className="text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Failed</h1>
              <p className="text-gray-400 mb-6">{message}</p>
              <button
                onClick={() => navigate('/members')}
                className="w-full bg-[#23F47C] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all"
              >
                Back to Members
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
